import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "react-query";
import { useHistory } from "react-router";
import { useRecoilState, useRecoilValue } from "recoil";
import CancelButton from "../../../../components/CancelButton";
import ConfirmBoxModal from "../../../../components/ConfirmBoxModal";
import CloseIconButton from "../../../../components/CloseIconButton";
import SubmitHookForm from "../../../../components/SubmitHookForm";
import { toast } from "../../../../components/toastNotification";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { mainSelectedTab } from "../../atoms";
import { CarrierAgreementMgmtTabs } from "../../carrierAgreementTabs";
import {
  rowForEditSecurity,
  SecurityAddState,
  securityAttachments,
  securityAttachmentsForDelete,
  SecurityEditState,
  securityFromOffice,
  SecuritySaving,
  setLinkStateSecurity,
  attachDisplay,
  AlarmModalState,
  CameraModalState,
  officeKeyModalState,
  DoorBuzzerModalState,
  HvacModalState,
  SafeModalState,
  securityOnchangeCategory,
  safeDeactivateModal,
  dbDeactivateModal,
  okDeactivateModal,
  alarmDeactivateModal,
  cameraDeactivateModal,
  hvacDeactivateModal,
  CardReaderModalState,
  addCardReaderForm,
  cardReaderFormState,
  addedCardReader,
  updatedCardReader,
  CardReaderForEdit,
  tempCameras,
  tempAlarms,
  tempFireAlarmSystems,
  FireAlarmSystemModalState,
} from "../atoms/atoms";
import useAddSecurity from "../hooks/useAddSecurity";
import useEditSecurity from "../hooks/useEditSecurity";
import { ISecurityDetails } from "../types";
import SecurityDetails from "./SecurityDetails";

interface SecurityFormProps {
  isAdd: boolean;
  isEdit: boolean;
  setIsAdd;
  setIsEdit;
  viewFromOffice: boolean;
  setIsCardReaderOpen: (value: boolean) => void;
}

export const SecurityForm: React.FC<SecurityFormProps> = ({
  isAdd,
  isEdit,
  setIsAdd,
  setIsEdit,
  setIsCardReaderOpen,
  viewFromOffice = false,
}) => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const [isLinkedState] = useRecoilState(setLinkStateSecurity);
  const attachments = useRecoilValue<(File | null)[]>(securityAttachments);
  const [attachmentsForDelete, setAttachmentsForDelete] = useRecoilState<any>(
    securityAttachmentsForDelete
  );
  const [isSaving, setIsSaving] = useRecoilState(SecuritySaving);
  const [saveModal, setSaveModal] = useState(false);
  const [isAddState, setAddState] = useRecoilState(SecurityAddState);

  const [cardReaderActionState] = useRecoilState(cardReaderFormState);

  if (cardReaderActionState !== "NONE") {
    setIsCardReaderOpen(true);
  } else {
    setIsCardReaderOpen(false);
  }

  const [, setEditState] = useRecoilState(SecurityEditState);
  const [rowForEdit, setRowForEdit] = useRecoilState(rowForEditSecurity);
  const [showFromOffice, setShowFromOffice] =
    useRecoilState(securityFromOffice);
  const userPermissions = useRecoilValue(permissionsSelector);
  const [, setSelectedIndex] = useRecoilState(mainSelectedTab);
  const [newData, setNewData] = useState<ISecurityDetails>();
  const attachmentDisplay = useRecoilValue<any>(attachDisplay);
  const showAlarmCardModal = useRecoilValue(AlarmModalState);
  const showCameraCardModal = useRecoilValue(CameraModalState);
  const showCardReaderCardModal = useRecoilValue(CardReaderModalState);
  const showFireAlarmSystemCardModal = useRecoilValue(
    FireAlarmSystemModalState
  );
  const showOfficeKeyCardModal = useRecoilValue(officeKeyModalState);
  const showDoorBuzzerCardModal = useRecoilValue(DoorBuzzerModalState);
  const [secCardReader, setSecCardReader] =
    useRecoilState<any>(addCardReaderForm);
  const [initialAddCameras, setInitialAddCameras] = useRecoilState(tempCameras);
  const [initialAddAlarms, setInitialAddAlarms] = useRecoilState(tempAlarms);
  const [initialAddFireAlarmSystems, setInitialAddFireAlarmSystems] =
    useRecoilState(tempFireAlarmSystems);
  const [, setCardReaderForEdit] = useRecoilState(CardReaderForEdit);
  const showHvacCardModal = useRecoilValue(HvacModalState);
  const showSafeCardModal = useRecoilValue(SafeModalState);
  const [onchangeCategory, setOnchangeCategory] = useRecoilState(
    securityOnchangeCategory
  );
  const showDeactivateSafeModal = useRecoilValue(safeDeactivateModal);
  const showDeactivateDbModal = useRecoilValue(dbDeactivateModal);
  const showDeactivateOkModal = useRecoilValue(okDeactivateModal);
  const showDeactivateAlarmModal = useRecoilValue(alarmDeactivateModal);
  const showDeactivateCameraModal = useRecoilValue(cameraDeactivateModal);
  const showDeactivateHvacModal = useRecoilValue(hvacDeactivateModal);
  const useFormMethods = useForm<ISecurityDetails>({
    defaultValues: { ...rowForEdit },
  });
  const {
    formState: { isDirty },
  } = useFormMethods;

  const handleLink = () => {
    history.push({
      pathname: "/officesettings/security",
    });
    window.location.reload();
  };

  // Add Security Initialization
  const {
    mutate: addSecurity,
    isSuccess: addSuccess,
    isError: addError,
    data: addResult,
    reset: resetAddData,
  } = useAddSecurity();

  //Edit Security Initialization/
  const {
    mutate: editSecurity,
    isSuccess: editSuccess,
    isError: editError,
    data: editResult,
    reset: resetEditData,
  } = useEditSecurity();

  const handleSave = () => {
    setSaveModal(false);
    const getLocationKey = (locations) => {
      return locations
        ?.map(
          (loc) =>
            `${loc.camerasAndLocationId}-${loc.cameraType1 ?? ""}-${
              loc.cameraType2 ?? ""
            }`
        )
        .sort()
        .join("|");
    };

    const hasDuplicate = initialAddCameras.some((cam, idx) => {
      return (
        initialAddCameras.findIndex(
          (otherCam, otherIdx) =>
            idx !== otherIdx &&
            cam.cameraMake?.trim() === otherCam.cameraMake?.trim() &&
            cam.cameraModel?.trim() === otherCam.cameraModel?.trim() &&
            cam.dvrTypeId === otherCam.dvrTypeId &&
            cam.dvrRecordingMax?.trim() === otherCam.dvrRecordingMax?.trim() &&
            cam.cameraSystemId === otherCam.cameraSystemId &&
            getLocationKey(cam.cameraLocationsDto) ===
              getLocationKey(otherCam.cameraLocationsDto)
        ) !== -1
      );
    });

    if (hasDuplicate) {
      setIsSaving(false);

      toast.fire(
        "Duplicate Camera",
        "A camera with the same details and locations already exists.",
        "error"
      );
      return;
    }

    ///
    if (isAddState) {
      addSecurity({
        ...newData,
        secCardReader,
        initialAddCameras,
        initialAddAlarms,
        initialAddFireAlarmSystems,
      });
      setAttachmentsForDelete([]);
    } else {
      setSaveModal(true);
      editSecurity(newData);
      setOnchangeCategory([]);
      setAttachmentsForDelete([]);
    }
  };

  // redirection back to office
  const handleLinkOffice = (moduleName) => {
    const url = `/carrieragreementmgmt/${moduleName}`;
    history.push(moduleName);
    let userTabs = [
      {
        to: "",
      },
    ];
    [...CarrierAgreementMgmtTabs].forEach((tabs) => {
      let temp = [...userPermissions].filter(
        (user) => user === tabs.permission
      );
      if (temp.length > 0)
        userTabs.push({
          to: tabs.to,
        });
    });

    userTabs.shift();
    const index = userTabs.findIndex((c) => c.to === url);
    setSelectedIndex(index);
  };

  const handleCancel = () => {
    if (showFromOffice) {
      setShowFromOffice(false);
      handleLinkOffice("officesetting");
    }
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setRowForEdit(undefined);
    setAddState(false);
    setEditState(false);
    setIsSaving(false);
    setIsAdd(false);
    setIsEdit(false);
    setAttachmentsForDelete([]);
    setOnchangeCategory([]);
  };

  if (addSuccess && addResult) {
    if (addResult.status === 501) {
      toast.fire("Failed", "Security record already exists.", "error");
      setIsSaving(!isSaving);
      setSaveModal(false);
    } else if (addResult.status === 201 || addResult.status === 200) {
      toast.fire("Success", "Security added successfully.", "success");
      setInitialAddCameras([]);
      setInitialAddAlarms([]);
      setInitialAddFireAlarmSystems([]);
      queryClient.invalidateQueries("GetSecurity");
      //ssearchRefetch();
      handleCancel();
      setAddState(false);
      setIsAdd(false);
    } else if (addResult.status === 500) {
      if (
        addResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available" ||
        addResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available"
      ) {
        toast.fire(
          "Warning",
          "New security added successfully, but " + addResult.data.message,
          "warning"
        );
        handleCancel();
      } else {
        toast.fire(
          "Failed",
          "Failed to add security! No data has been saved.",
          "error"
        );
      }
      queryClient.invalidateQueries("GetSecurity");
      setAddState(false);
      setIsAdd(false);
    } else {
      toast.fire(
        "Server Error",
        "Failed to add security! No data has been saved.",
        "error"
      );
      queryClient.invalidateQueries("GetSecurity");
    }
    resetAddData();
    setSaveModal(false);
    setIsSaving(false);
  }
  if (addError) {
    setIsSaving(false);
    setSaveModal(false);
    toast.fire(
      "Server Error",
      "Failed to add security! No data has been saved.",
      "error"
    );
    queryClient.invalidateQueries("GetSecurity");
    setAddState(false);
  }

  //Edit result
  if (editSuccess && editResult) {
    if (editResult && editResult.status === 501) {
      toast.fire("Failed", "Security record already exists.", "error");
      setIsSaving(!isSaving);
      setSaveModal(false);
    } else if (
      (editResult && editResult.status === 201) ||
      editResult.status === 200
    ) {
      toast.fire("Success", "Security updated successfully.", "success");
      queryClient.invalidateQueries("GetSecurity");
      //searchRefetch();
      handleCancel();
      setRowForEdit(undefined);
      setEditState(false);
      setIsEdit(false);
      setOnchangeCategory([]);
    } else if (editResult.status === 500) {
      if (
        editResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available" ||
        editResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available"
      ) {
        toast.fire(
          "Warning",
          "New security updated successfully, but " + editResult.data.message,
          "warning"
        );
        setIsSaving(false);
        handleCancel();
        setRowForEdit(undefined);
      } else {
        setIsSaving(false);
        setRowForEdit(undefined);
        toast.fire(
          "Failed",
          "Failed to update security! No data has been made.",
          "error"
        );
      }
      queryClient.invalidateQueries("GetSecurity");
      setEditState(false);
      setIsEdit(false);
    } else {
      toast.fire(
        "Server Error",
        "Failed to update security! No data has been made.",
        "error"
      );
      queryClient.invalidateQueries("GetSecurity");
      setEditState(false);
      setIsEdit(false);
    }
    resetEditData();
  }
  if (editError) {
    setIsSaving(false);
    setSaveModal(false);
    setIsEdit(false);
    toast.fire(
      "Server Error",
      "Failed to update security! No data has been made.",
      "error"
    );
    queryClient.invalidateQueries("GetSecurity");
    setEditState(false);
    setIsEdit(false);
  }

  //Functions for validation of fileCategory and Attachments
  const validateAttachment = (data) => {
    var isValid = true;
    for (var x = 0; x < attachmentDisplay?.length; x++) {
      if (
        attachmentDisplay.filter(
          (x) => x.fileCategoryId === data.fileCategoryList[x]?.id
        )
      ) {
        isValid = false;
      }
    }

    return isValid;
  };

  const validateFileCatergory = (data) => {
    var valid = false;
    for (var x = 0; x < data.fileCategoryList?.length; x++) {
      if (
        data.fileCategoryList[x]?.id === undefined ||
        data.fileCategoryList[x]?.id === null
      ) {
        valid = true;
      }
    }
    return valid;
  };

  const validateFileContent = (data) => {
    var isValid = true;

    var arr = Array.from(
      new Set(attachmentDisplay.map((x) => x.fileCategoryId))
    ).length;
    var dataFile = data?.fileCategoryList?.map((x) => x.id).length;
    if (arr !== dataFile) {
      isValid = false;
    }
    return isValid;
  };

  const validateAttachmentContent = (data) => {
    var valid = false;
    for (var x = 0; x < attachmentDisplay?.length; x++) {
      if (
        attachmentDisplay.map((x) => x.fileCategoryId).length ===
          data.fileNames?.map((x) => x.fileCategoryId).length &&
        attachments.length === 0
      ) {
        valid = true;
      }
    }
    return valid;
  };

  const onSubmit = (data: ISecurityDetails) => {
    let validate = validateAttachment(data);
    let isValid = validateFileCatergory(data);
    let isFileHasContent = validateFileContent(data);
    let isAttachmentContent = validateAttachmentContent(data);
    setNewlyAddedCardReader([]);
    var validateData = true;
    var arr = Array.from(
      new Set(attachmentDisplay.map((x) => x.fileCategoryId))
    ).length;
    var file = data?.fileCategoryList?.length;
    if (arr !== file) {
      validateData = false;
    } else {
      validateData = true;
    }

    const rCateg = data.fileCategoryList?.map((x) => x.id);

    const filesForDelete = data.fileNames?.filter(
      (file: any) => !rCateg?.includes(file.fileCategoryId)
    );

    const finalFilesForDelete =
      attachmentsForDelete && attachmentsForDelete?.length > 0
        ? filesForDelete && filesForDelete?.length > 0
          ? Array.from(
              new Set(
                [...filesForDelete!, ...attachmentsForDelete].map(
                  (obj) => obj?.id
                )
              )
            ).map((id) => {
              return [...filesForDelete!, ...attachmentsForDelete].find(
                (obj) => obj?.id === id
              );
            })
          : attachmentsForDelete
        : filesForDelete;

    setNewData({
      ...data,
      attachments: attachments,
      attachmentDisplay: attachmentDisplay,
      attachmentsForDelete: attachmentsForDelete,
      onchangeCategory: onchangeCategory,
    });

    //To validate if fileCategory has attachments and onchange
    if (
      (isValid === false &&
        validate === true &&
        isFileHasContent === false &&
        !isAdd) ||
      (isValid === true &&
        validate === false &&
        isFileHasContent === false &&
        !isAdd) ||
      (isValid === false &&
        validate === false &&
        isFileHasContent === false &&
        validateData === false &&
        !isAdd) ||
      //For toast not to show upon clicking the edit on the cards
      (isFileHasContent === true &&
        isDirty === true &&
        isAttachmentContent === true &&
        attachments?.length === 0 &&
        onchangeCategory?.length === 0 &&
        !isAdd &&
        !showAlarmCardModal &&
        !showCameraCardModal &&
        !showCardReaderCardModal &&
        !showFireAlarmSystemCardModal &&
        !showDoorBuzzerCardModal &&
        !showHvacCardModal &&
        !showSafeCardModal &&
        !showOfficeKeyCardModal &&
        !showDeactivateSafeModal &&
        !showDeactivateOkModal &&
        !showDeactivateDbModal &&
        !showDeactivateAlarmModal &&
        !showDeactivateCameraModal &&
        !showDeactivateHvacModal)
    ) {
      setIsSaving(false);
      setSaveModal(false);
      toast.fire(
        "Information",
        "Please fill the file category field and choose a file to proceed.",
        "info"
      );
    }

    if (isAdd) {
      if (
        (isValid === false &&
          validate === true &&
          isFileHasContent === false) ||
        (isValid === true &&
          validate === false &&
          isFileHasContent === false) ||
        (isValid === false &&
          validate === false &&
          isFileHasContent === false &&
          validateData === false)
      ) {
        setIsSaving(false);
        setSaveModal(false);
        toast.fire(
          "Information",
          "Please fill the file category field and choose a file to proceed.",
          "info"
        );
      } else if (validateData) {
        setSaveModal(true);
      } else {
        setSaveModal(true);
      }
    } else if (
      (validateData && finalFilesForDelete?.length > 0 && isEdit) ||
      (validateData && attachments?.length > 0 && isEdit) ||
      (isValid === true &&
        validate === true &&
        isFileHasContent === false &&
        isEdit &&
        !showAlarmCardModal &&
        !showCameraCardModal &&
        !showCardReaderCardModal &&
        !showFireAlarmSystemCardModal &&
        !showDoorBuzzerCardModal &&
        !showHvacCardModal &&
        !showSafeCardModal &&
        !showOfficeKeyCardModal &&
        !showDeactivateSafeModal &&
        !showDeactivateOkModal &&
        !showDeactivateDbModal &&
        !showDeactivateAlarmModal &&
        !showDeactivateCameraModal &&
        !showDeactivateHvacModal) ||
      (onchangeCategory?.length > 0 && isEdit && validateData)
    ) {
      setSaveModal(true); //bug fix
    } else {
      setSaveModal(false);
    }

    //No data change validation
    if (
      (attachments?.length === 0 &&
        !isDirty &&
        attachmentsForDelete?.length === 0 &&
        !isAdd &&
        !showAlarmCardModal &&
        !showCameraCardModal &&
        !showCardReaderCardModal &&
        !showFireAlarmSystemCardModal &&
        !showDoorBuzzerCardModal &&
        !showHvacCardModal &&
        !showSafeCardModal &&
        !showOfficeKeyCardModal &&
        !showDeactivateSafeModal &&
        !showDeactivateOkModal &&
        !showDeactivateDbModal &&
        !showDeactivateAlarmModal &&
        !showDeactivateCameraModal &&
        !showDeactivateHvacModal) ||
      (arr === file &&
        !isAdd &&
        !showAlarmCardModal &&
        !showCameraCardModal &&
        showCardReaderCardModal &&
        !showFireAlarmSystemCardModal &&
        !showDoorBuzzerCardModal &&
        !showHvacCardModal &&
        !showSafeCardModal &&
        !showOfficeKeyCardModal &&
        !showDeactivateSafeModal &&
        !showDeactivateOkModal &&
        !showDeactivateDbModal &&
        !showDeactivateAlarmModal &&
        !showDeactivateCameraModal &&
        !showDeactivateHvacModal &&
        attachments?.length === 0 &&
        onchangeCategory?.length === 0 &&
        attachmentDisplay?.length === data.fileNames?.length)
    ) {
      toast.fire("Saving Failed", "No changes has been made.", "warning");
      setIsSaving(false);
      setSaveModal(false);
    }
  };
  const [newlyAddedCardReader, setNewlyAddedCardReader] =
    useRecoilState(addedCardReader);
  const [newlyUpdatedCardReader, setNewlyUpdatedCardReader] =
    useRecoilState(updatedCardReader);
  return (
    <div className="">
      {(rowForEdit || isAddState) && (
        <div className="">
          <div
            className={
              viewFromOffice
                ? " p-3 space-y-2 border-l-2 border-primary box-shadow "
                : " p-3 space-y-2 border-l-2 border-primary box-shadow"
            }
          >
            <FormProvider {...useFormMethods}>
              <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
                <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg mx-2 pb-1">
                  <div className="font-bold text-primary text-md">
                    {isAdd || isAddState ? "Add Security" : "Edit Security"}
                  </div>
                  {isAdd || isAddState ? (
                    <div className="flex justify-between gap-2 mr-2">
                      <SubmitHookForm
                        type="submit"
                        disabled={isSaving}
                        buttonText={isSaving ? "Saving.." : "Save"}
                        className={undefined}
                        onClick={undefined}
                      />
                      <CancelButton
                        onClick={() => {
                          if (isLinkedState) {
                            handleLink();
                          }
                          handleCancel();
                          setSecCardReader([]);
                          setInitialAddCameras([]);
                          setInitialAddAlarms([]);
                          setInitialAddFireAlarmSystems([]);
                        }}
                      />
                    </div>
                  ) : (
                    <div className="relative -mr-5 -mt-12">
                      <CloseIconButton
                        onClick={() => {
                          handleCancel();
                          setNewlyAddedCardReader([]);
                          setNewlyUpdatedCardReader([]);

                          setSecCardReader([]);
                          setInitialAddCameras([]);
                          setInitialAddAlarms([]);
                          setInitialAddFireAlarmSystems([]);
                          setCardReaderForEdit(false);
                        }}
                      />
                    </div>
                  )}
                </div>
                <div className="border-b-2 border-secondary my-1 mx-2"></div>
                <div className="text-xs text-secondary italic font-normal mt-2 ml-2">
                  <b> Note:</b> All fields with * are required to fill.
                </div>
                <div className="">
                  <SecurityDetails
                    rowForEdit={rowForEdit}
                    isEdit={isEdit}
                    filesImages={
                      rowForEdit?.fileCategoryList &&
                      rowForEdit?.fileCategoryList?.length > 0
                        ? rowForEdit?.fileCategoryList
                        : undefined
                    }
                  />
                </div>
              </form>
            </FormProvider>
          </div>
          {saveModal && (
            <ConfirmBoxModal
              ModalTitle={isAddState ? "Add Security" : "Edit Security"}
              qConfirmation={`Are you sure you want to ${
                isAddState ? "Add " : "Update "
              } this data?`}
              isOpen={saveModal}
              onClose={() => {
                setSaveModal(false);
                setIsSaving(false);
              }}
              onClickFunction={() => {
                handleSave();
              }}
              cancelButtonRef={undefined}
            />
          )}
        </div>
      )}
    </div>
  );
};
